<template>
  <header class="relative z-[160]">
    <div
      v-if="envDisplay.show"
      :class="envDisplay.bannerClass"
      class="text-center py-0.5 text-[10px] leading-none font-bold uppercase tracking-widest w-full"
    >
      {{ envDisplay.bannerText }}
    </div>

    <div
      v-if="authStore.user.isImpersonating"
      class="text-center py-0.5 text-[10px] leading-none font-bold uppercase tracking-widest w-full bg-warning text-white"
    >
      ⚠️ Impersonating {{ authStore.user.firstName }} {{ authStore.user.lastName }} — changes are made on their behalf ⚠️
    </div>

    <nav
      id="navbar"
      class="relative flex flex-shrink-0 bg-white container-padding py-2 h-[53px] shadow-bottom-light"
    >
      <div class="flex items-center justify-between w-full h-full">
        <!-- Left aligned items -->
        <div class="flex items-center h-full">
          <!-- Brand image and link -->
          <div>
            <RouterLink
              class="py-0 flex items-center no-underline"
              :to="{ name: 'app_plan_overview', params: { municipality: currentMunicipality?.id } }"
            >
              <div class="flex items-center">
                <img
                  :src="Logo"
                  class="h-[34px] w-fit"
                >
              </div>
            </RouterLink>
          </div>

          <!-- Select municipality -->
          <RouterLink
            v-if="authStore.user.municipalities?.length > 1"
            :to="{ name: 'app_municipality_select' }"
            class="flex items-center gap-2 no-underline"
          >
            <span class="hidden lg:block ml-3 py-1 px-2 !bg-secondary/20 !text-primary border !border-secondary/40 !mb-0 hover:cursor-pointer rounded font-semibold text-sm">
              {{ currentMunicipality?.name }}
            </span>

            <span class="underline text-primary ml-2 text-xs">Switch municipality</span>
          </RouterLink>
        </div>

        <!-- Right aligned items -->
        <div class="flex items-center text-sm font-medium">
          <div class="flex justify-end items-center gap-4 mr-2">
            <RouterLink
              class="no-underline text-chrome-dark hover:text-secondary py-2 border-b-2 border-b-transparent hover:border-b-secondary duration-200"
              :to="{ name: 'app_plan_overview', params: { municipality: currentMunicipality?.id } }"
            >
              Plan Overview
            </RouterLink>

            <RouterLink
              v-if="isMunicipalAdministrator && !isSystemAdministrator"
              class="no-underline text-chrome-dark hover:text-secondary py-2 border-b-2 border-b-transparent hover:border-b-secondary duration-200"
              :to="{ name: 'app_user_new' }"
            >
              Add a User
            </RouterLink>

            <AppDropdown
              v-if="isSystemAdministrator"
              class="nav-cog-dropdown"
            >
              <template #trigger>
                <FontAwesomeIcon
                  class="text-xl hover:text-secondary text-lg"
                  :icon="faCog"
                />

                <span class="sr-only">Settings</span>
              </template>

              <AppDropdownItem
                :to="{ name: 'app_user_list' }"
                text="Users"
              />

              <AppDropdownItem
                :to="{ name: 'app_municipality_list' }"
                text="Municipalities"
              />

              <AppDropdownItem
                :to="{ name: 'app_framework_list' }"
                text="Frameworks"
              />

              <AppDropdownItem
                :to="{ name: 'app_setting_list' }"
                text="Settings"
              />
            </AppDropdown>
          </div>

          <!-- Profile -->
          <AppDropdown class="nav-profile-dropdown">
            <template #trigger>
              <div class="flex gap-2 items-center font-semibold hover:bg-secondary-subtle p-2 rounded-sm">
                <span class="bg-secondary text-white rounded-full w-8 h-8 flex items-center justify-center text-xs">
                  {{ authStore.user.firstName[0].toUpperCase() }}{{ authStore.user.lastName[0].toUpperCase() }}
                </span>

                <span class="text-chrome-dark text-sm">
                  {{ authStore.user.firstName }} {{ authStore.user.lastName }}
                </span>
              </div>
            </template>

            <AppDropdownItem
              :to="{ name: 'app_auth_change_password' }"
              text="Change password"
            />

            <AppDropdownItem
              v-if="authStore.user.isImpersonating"
              text="Exit impersonation"
              @click="exitImpersonation"
            />

            <AppDropdownItem
              text="Logout"
              @click="logout"
            />
          </AppDropdown>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';

import { faCog } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import Logo from '@/assets/images/logos/living-plans-logo.svg';
import { useHttp } from '@/composables';
import config from '@/config';
import { Role } from '@/const';
import { useAuthStore } from '@/store';

const authStore = useAuthStore();
const router = useRouter();

const currentMunicipality = computed(() => authStore.currentMunicipality);
const isSystemAdministrator = computed(() => authStore.user.roles.includes(Role.SystemAdministrator));
const isMunicipalAdministrator = computed(() => authStore.user.roles.includes(Role.MunicipalAdministrator));

const envDisplay = computed(() => {
  if (config.env === 'staging') {
    return {
      show: true,
      bannerClass: 'bg-[#d946ef] text-white',
      bannerText: '⚠️ Staging Environment — Test Data Only ⚠️',
    };
  }

  if (config.env === 'development') {
    return {
      show: true,
      bannerClass: 'bg-success text-white',
      bannerText: '🚧 Local Development Environment 🚧',
    };
  }

  return { show: false };
});

const logout = async () => {
  const response = await useHttp().post('auth/logout');

  if (response.statusCode === HttpStatusCode.Ok) {
    authStore.user = null;
    await router.push({ name: 'app_auth_login' });
  }
};

const exitImpersonation = async () => {
  const response = await useHttp().get('/profile', { _switch_user: '_exit' });

  if (response.statusCode === HttpStatusCode.Ok) {
    authStore.user = response.data;

    const currentMunicipality = response.data.municipalities.find(municipality => municipality.current);

    await router.push({ name: 'app_plan_overview', params: { municipality: currentMunicipality.id } });
  }
};

</script>
